// Chairman Briefing — Bvndle KPIs and Track Owners.
//
// Layout mirrors strategy/Bvndle-Chairman-Briefing-29May2026.docx exactly:
//
//   Header        BVNDLE COMMAND CENTRE
//                 Bvndle KPIs & Track Owners
//                 Bvndle ManCo  ·  <today>  ·  Sourced from ...
//
//   Per KPI block (S1 → S2 → S3 → D1 → D2 → D3):
//     CODE  Title  (one row, code chip + bold title)
//     Question  (italic, smaller)
//     OWNERS:    [names]
//     ACTIONABLE INSIGHT:   [one-sentence data-anchored claim, bold]
//     Track-owner row(s):  NAME  ·  scope  ·  STATUS pill
//     BLOCKERS:                [one line]
//     WHAT WE'RE DOING ABOUT IT: [one line]
//
//   ADDITIONAL COMMITMENTS section at the bottom.
//
// Differences from the 29-May source:
//   - Date is today's, not 29 May, but "Sourced from ..." cites the
//     latest underlying docs (1-pager 2 Jun 2026 + Product Priority +
//     Tasks Tracker).
//   - Actionable-insight numbers are refreshed against the 2 Jun 1-pager
//     (Jendol falls from 34% to 17% of YTD; El Padrino is now top at 27%).
//   - D1's ACTIONABLE INSIGHT becomes live-sensitive when the
//     `live-spend-share-by-category` Metabase query lands — the page
//     auto-overrides the snapshot phrasing with the freshest numbers.
//   - "Download Word" emits a .docx mirror via docx-js (same pattern as
//     the Redemption 1-pager); "Print → PDF" still available.

import { saveAs } from "file-saver";
import { toPng } from "html-to-image";
import { useMemo, useState } from "react";

import { ChairmanKpiChart } from "../components/chairman/ChairmanKpiChart";
import { buildChairmanPdf } from "../lib/chairman-pdf";
import { useLiveCategoryStats } from "../lib/live-category-stats";
import { runCatalogQuery } from "../lib/query-catalog";

const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const hasTauri =
  typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

interface SaveResult {
  path: string;
  bytes: number;
}

// Native save: open the OS save dialog (so the user grants us a path
// explicitly — no TCC ambush mid-write to Downloads), then ask Rust to
// write the bytes there. Rust verifies the written byte count and
// returns it so we can show a confirmation toast.
async function saveViaTauri(
  filename: string,
  bytes: Uint8Array,
  ext: "docx" | "pdf",
): Promise<SaveResult | null> {
  const core = await import("@tauri-apps/api/core");
  const dialog = await import("@tauri-apps/plugin-dialog");

  const filterName = ext === "docx" ? "Word Document" : "PDF Document";
  const chosen = await dialog.save({
    defaultPath: filename,
    filters: [{ name: filterName, extensions: [ext] }],
  });
  if (!chosen) return null; // user cancelled

  return core.invoke<SaveResult>("save_report_to_path", {
    targetPath: chosen,
    bytes: Array.from(bytes),
  });
}
import {
  ADDITIONAL_COMMITMENTS,
  DEMAND_KPIS,
  SUPPLY_KPIS,
  type AdditionalCommitment,
  type KpiBrief,
  type KpiTrackOwner,
} from "../lib/partner-rollout";
import { buildChairmanDocx } from "../lib/chairman-docx";
import {
  HEADLINE,
  PARTNER_CATEGORIES,
  rollupAll,
} from "../lib/partners-current";

const SOURCED_FROM =
  "Redemption Partners 1-pager (2 Jun 2026) + Product Priority + Tasks Tracker";

export function ChairmanReportPage() {
  const today = new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  // Live-state for D1's actionable-insight override. When the Metabase
  // query lands we re-rank the categories from live coin counts and
  // synthesise a fresh insight string; otherwise we use the static one
  // populated in partner-rollout.ts.
  const live = useLiveCategoryStats("live-spend-share-by-category");

  // Compose the per-KPI briefs, optionally overriding D1's insight.
  const supply = SUPPLY_KPIS;
  const demand = useMemo(() => {
    return DEMAND_KPIS.map((k) => {
      if (k.code !== "D1") return k;
      const fresh = composeLiveD1Insight(live);
      return fresh ? { ...k, actionableInsight: fresh } : k;
    });
  }, [live]);

  const [dlState, setDlState] = useState<
    | { kind: "idle" }
    | { kind: "working"; step: string }
    | { kind: "ok"; path: string }
    | { kind: "err"; msg: string }
  >({ kind: "idle" });

  async function onDownload() {
    try {
      // ---- 1. Force-refresh live data ----------------------------------
      // The page's live hooks load on mount; if the cache is stale or the
      // query hasn't returned yet, we'd be embedding old numbers. Re-run
      // the live query right now so what we ship is what live says now.
      setDlState({ kind: "working", step: "Refreshing live data…" });
      let freshLiveByCode: Record<string, number> = {};
      try {
        const res = await runCatalogQuery("live-spend-share-by-category");
        if (res.status === "live") {
          for (const row of res.rows) {
            const code = String(row[0] ?? "").trim();
            const val = Number(row[1]);
            if (code && Number.isFinite(val)) freshLiveByCode[code] = val;
          }
        }
      } catch (e) {
        console.warn("[chairman-docx] live refresh failed; using snapshot", e);
      }
      void freshLiveByCode; // used implicitly via the in-app hook update

      // ---- 2. Wait one frame so any chart re-renders settle ------------
      await new Promise((r) => requestAnimationFrame(() => r(null)));

      // ---- 3. Capture each KPI's chart DOM as PNG ----------------------
      setDlState({ kind: "working", step: "Capturing charts…" });
      const codes = [...supply, ...demand].map((k) => k.code);
      const chartImages: Record<string, string> = {};
      const failures: string[] = [];
      await Promise.all(
        codes.map(async (code) => {
          const node = document.getElementById(`cb-chart-${code}`);
          if (!node) {
            failures.push(`${code}: node not in DOM`);
            return;
          }
          try {
            const png = await toPng(node, {
              backgroundColor: "#ffffff",
              pixelRatio: 2,
              cacheBust: true,
              skipFonts: true,
            });
            if (!png || !png.startsWith("data:image/png")) {
              failures.push(`${code}: capture returned non-PNG`);
              return;
            }
            chartImages[code] = png;
          } catch (e) {
            failures.push(`${code}: ${String(e)}`);
          }
        }),
      );
      if (failures.length > 0) {
        console.warn("[chairman-docx] chart capture failures:", failures);
      }

      // ---- 4. Build the .docx ------------------------------------------
      setDlState({ kind: "working", step: "Building Word document…" });
      const docxBlob = await buildChairmanDocx({
        generatedOn: today,
        sourcedFrom: SOURCED_FROM,
        supply,
        demand,
        additional: ADDITIONAL_COMMITMENTS,
        liveState: live.state,
        chartImages,
      });

      // ---- 5. Save natively via Tauri command --------------------------
      const stamp = new Date().toISOString().slice(0, 10);
      const filename = `Bvndle-Chairman-Briefing-${stamp}.docx`;

      if (hasTauri) {
        setDlState({ kind: "working", step: "Choose location…" });
        const bytes = new Uint8Array(await docxBlob.arrayBuffer());
        const result = await saveViaTauri(filename, bytes, "docx");
        if (!result) {
          setDlState({ kind: "idle" });
          return;
        }
        setDlState({
          kind: "ok",
          path: `${fmtBytes(result.bytes)} · ${result.path}`,
        });
      } else {
        // Browser-dev fallback (no Tauri shell): use file-saver +
        // explicit MIME type — best we can do in plain WebKit.
        const typedBlob = new Blob([docxBlob], { type: DOCX_MIME });
        saveAs(typedBlob, filename);
        setDlState({ kind: "ok", path: `~/Downloads/${filename}` });
      }

      if (failures.length > 0) {
        setDlState({
          kind: "err",
          msg: `Saved, but ${failures.length} chart(s) couldn't be captured. See console for detail.`,
        });
        setTimeout(() => setDlState({ kind: "idle" }), 6000);
      } else {
        setTimeout(() => setDlState({ kind: "idle" }), 4000);
      }
    } catch (e) {
      console.error("[chairman-docx] download failed", e);
      setDlState({ kind: "err", msg: String(e) });
      setTimeout(() => setDlState({ kind: "idle" }), 6000);
    }
  }

  async function onPdfDownload() {
    try {
      setDlState({ kind: "working", step: "Refreshing live data…" });
      try {
        await runCatalogQuery("live-spend-share-by-category");
      } catch (e) {
        console.warn("[chairman-pdf] live refresh failed; using snapshot", e);
      }

      // Let any chart re-renders settle before we snapshot the DOM.
      await new Promise((r) => requestAnimationFrame(() => r(null)));

      setDlState({ kind: "working", step: "Building PDF…" });
      const node = document.getElementById("cb-report-page");
      if (!node) throw new Error("report-page node not found in DOM");
      const pdfBlob = await buildChairmanPdf({ node, pixelRatio: 2 });

      const stamp = new Date().toISOString().slice(0, 10);
      const filename = `Bvndle-Chairman-Briefing-${stamp}.pdf`;

      if (hasTauri) {
        setDlState({ kind: "working", step: "Choose location…" });
        const bytes = new Uint8Array(await pdfBlob.arrayBuffer());
        const result = await saveViaTauri(filename, bytes, "pdf");
        if (!result) {
          setDlState({ kind: "idle" });
          return;
        }
        setDlState({
          kind: "ok",
          path: `${fmtBytes(result.bytes)} · ${result.path}`,
        });
      } else {
        const typedBlob = new Blob([pdfBlob], { type: "application/pdf" });
        saveAs(typedBlob, filename);
        setDlState({ kind: "ok", path: `~/Downloads/${filename}` });
      }
      setTimeout(() => setDlState({ kind: "idle" }), 4000);
    } catch (e) {
      console.error("[chairman-pdf] download failed", e);
      setDlState({ kind: "err", msg: String(e) });
      setTimeout(() => setDlState({ kind: "idle" }), 6000);
    }
  }

  return (
    <>
      <style>{printCss}</style>

      <div className="report-toolbar no-print">
        <div>
          <strong>Chairman briefing · preview</strong>
          <span>{" · Bvndle KPIs & track owners"}</span>
          {live.state === "live" && (
            <span className="rep-live-chip rep-live-chip-on">LIVE</span>
          )}
          {live.state === "loading" && (
            <span className="rep-live-chip rep-live-chip-load">LOADING</span>
          )}
          {(live.state === "fallback" || live.state === "error") && (
            <span className="rep-live-chip rep-live-chip-off">SNAPSHOT</span>
          )}
        </div>
        <div className="report-toolbar-right">
          {dlState.kind === "working" && (
            <span className="dl-toast dl-toast-working">{dlState.step}</span>
          )}
          {dlState.kind === "ok" && (
            <span className="dl-toast dl-toast-ok">Saved · {dlState.path}</span>
          )}
          {dlState.kind === "err" && (
            <span className="dl-toast dl-toast-err">{dlState.msg}</span>
          )}
          <button
            type="button"
            className="report-btn"
            onClick={onDownload}
            disabled={dlState.kind === "working"}
          >
            ↓ Download Word
          </button>
          <button
            type="button"
            className="report-print"
            onClick={onPdfDownload}
            disabled={dlState.kind === "working"}
          >
            ↓ Save PDF
          </button>
        </div>
      </div>

      <article className="report-page" id="cb-report-page">
        <header className="report-head">
          <div className="report-eyebrow">BVNDLE COMMAND CENTRE</div>
          <h1 className="report-title">Bvndle KPIs &amp; Track Owners</h1>
          <div className="report-meta">
            <span>Bvndle ManCo</span>
            <span>·</span>
            <span>{today}</span>
            <span>·</span>
            <span>Sourced from {SOURCED_FROM}</span>
          </div>
        </header>

        {/* SUPPLY */}
        <SideBanner side="supply" subtitle="What moves the needle for issuance" />
        {supply.map((kpi, i) => (
          <div key={kpi.code}>
            <KpiBlock kpi={kpi} side="supply" />
            {i < supply.length - 1 && <KpiDivider />}
          </div>
        ))}

        {/* DEMAND */}
        <SideBanner side="demand" subtitle="What moves the needle for redemption" />
        {demand.map((kpi, i) => (
          <div key={kpi.code}>
            <KpiBlock kpi={kpi} side="demand" />
            {i < demand.length - 1 && <KpiDivider />}
          </div>
        ))}

        {/* Additional commitments */}
        <section className="addl">
          <div className="addl-head">ADDITIONAL COMMITMENTS</div>
          {ADDITIONAL_COMMITMENTS.map((c) => (
            <AdditionalRow key={c.title} item={c} />
          ))}
        </section>

        {/* Signoff */}
        <div className="report-signoff">
          <div className="signoff-row">
            <div className="signoff-block">
              <div className="signoff-label">Chairman</div>
              <div className="signoff-line" />
              <div className="signoff-label" style={{ marginTop: 3 }}>
                Nonso Okpala
              </div>
            </div>
            <div className="signoff-block">
              <div className="signoff-label">Date</div>
              <div className="signoff-line" />
            </div>
            <div className="signoff-block">
              <div className="signoff-label">Notes</div>
              <div className="signoff-note-lines">
                <div />
                <div />
              </div>
            </div>
          </div>
        </div>

        <div className="report-foot">
          <span>BVNDLE COMMAND CENTRE</span>
          <span>POWERED BY VFD GROUP</span>
        </div>
      </article>
    </>
  );
}

// ---------------------------------------------------------------------------
// Live D1 insight synthesis
// ---------------------------------------------------------------------------

// Synthesise a fresh D1 insight from the live category-share query —
// BUT only when the live result is rich enough to produce a sentence
// that's worth a chairman's eyeballs. If it isn't, we fall back to the
// curated static insight (which is at least correct against the 1-pager
// snapshot).
//
// Quality bar — all of:
//   - at least 3 categories with > 0 coin share
//   - top-3 combined ≥ 60% of total (so the sentence "X% lives in Top1 +
//     Top2 + Top3" makes structural sense — anything below 60% means
//     the live regex isn't classifying enough and the prose would lie)
//   - top-1 < 90% (single-category dominance = classifier failure)
function composeLiveD1Insight(
  live: ReturnType<typeof useLiveCategoryStats>,
): string | null {
  if (live.state !== "live") return null;
  const byCode = live.byCode;
  const total = Object.values(byCode).reduce((s, n) => s + n, 0);
  if (total <= 0) return null;

  const ranked = PARTNER_CATEGORIES
    .map((c) => ({
      label: c.label.replace(/^[A-Za-z]+ · /, "").replace(/ &.*$/, ""),
      coins: byCode[c.code] ?? 0,
    }))
    .filter((x) => x.coins > 0)
    .sort((a, b) => b.coins - a.coins);

  // Quality gates — keep the curated static if any fail.
  if (ranked.length < 3) return null;
  const top3 = ranked.slice(0, 3);
  const top1 = top3[0];
  if (!top1) return null;
  const top3Share = top3.reduce((s, r) => s + r.coins, 0) / total;
  const top1Share = top1.coins / total;
  if (top3Share < 0.60 || top1Share > 0.90) return null;

  const activeCount = ranked.length;
  const silentCount = PARTNER_CATEGORIES.length - activeCount;
  return (
    `${(top3Share * 100).toFixed(0)}% of YTD redemption lives in ` +
    `${top3.map((r) => r.label).join(" + ")}. ` +
    `Spreading the next wave of activation across the ` +
    `${silentCount} silent categories is the way to broaden the base.`
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function KpiBlock({ kpi, side }: { kpi: KpiBrief; side: "supply" | "demand" }) {
  const bandClass =
    side === "supply" ? "kpi-band-supply" : "kpi-band-demand";
  return (
    <div className="cb-kpi">
      {/* LEFT column: title band + question + chart + track owners.
          RIGHT column: OWNERS · INSIGHT · BLOCKERS · WHAT WE'RE DOING,
          all stacked together so they sit next to the track owners. */}
      <div className="kpi-row">
        <div className="kpi-col-left">
          <div className={`kpi-band ${bandClass}`}>
            <span className="kpi-chip">{kpi.code}</span>
            <span className="kpi-title">{kpi.title}</span>
          </div>
          <div className="kpi-question">{kpi.question}</div>
          <div className="kpi-chart" id={`cb-chart-${kpi.code}`}>
            <ChairmanKpiChart kpiCode={kpi.code} />
          </div>
          {kpi.trackOwners.length > 0 && (
            <div className="kpi-tracks">
              {kpi.trackOwners.map((t, i) => (
                <TrackOwnerRow key={i} owner={t} />
              ))}
            </div>
          )}
        </div>

        <div className="kpi-col-right">
          <div className="kpi-meta">
            <div className="kpi-meta-label">OWNERS:</div>
            <div className="kpi-meta-val">{kpi.owners}</div>
          </div>
          <div className="kpi-meta">
            <div className="kpi-meta-label">ACTIONABLE INSIGHT:</div>
            <div className="kpi-meta-val">
              <strong>{kpi.actionableInsight}</strong>
            </div>
          </div>
          {kpi.blockers && (
            <div className="kpi-meta">
              <div className="kpi-meta-label kpi-meta-label-bad">BLOCKERS:</div>
              <div className="kpi-meta-val kpi-meta-val-bad">{kpi.blockers}</div>
            </div>
          )}
          <div className="kpi-meta">
            <div className="kpi-meta-label">WHAT WE'RE DOING ABOUT IT:</div>
            <div className="kpi-meta-val">{kpi.doing}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SideBanner({
  side,
  subtitle,
}: {
  side: "supply" | "demand";
  subtitle: string;
}) {
  const chipText = side === "supply" ? "SUPPLY" : "DEMAND";
  const title = side === "supply" ? "SUPPLY SIDE" : "DEMAND SIDE";
  return (
    <div className={`side-banner side-banner-${side}`}>
      <span className="side-banner-chip">{chipText}</span>
      <span className="side-banner-title">{title}</span>
      <span className="side-banner-sub">{subtitle}</span>
    </div>
  );
}

function KpiDivider() {
  return <div className="kpi-divider" />;
}

function TrackOwnerRow({ owner }: { owner: KpiTrackOwner }) {
  return (
    <div className="kpi-track-row">
      <span className="kpi-track-name">{owner.name}</span>
      <span className="kpi-track-sep">·</span>
      <span className="kpi-track-scope">{owner.scope}</span>
      <span className={`kpi-track-status kpi-track-status-${owner.status}`}>
        {owner.status.replace("-", " ").toUpperCase()}
      </span>
    </div>
  );
}

function AdditionalRow({ item }: { item: AdditionalCommitment }) {
  return (
    <div className="addl-row">
      <div className="addl-row-head">
        <span className="addl-row-title">{item.title}</span>
        <span className={`addl-status addl-status-${item.status}`}>
          {item.status.toUpperCase()}
        </span>
        <span className="addl-owner">{item.owner}</span>
      </div>
      <div className="addl-body">{item.body}</div>
    </div>
  );
}

// Headline counts kept around so they're available for the docx builder
// downstream — not rendered on the in-app preview (the source briefing
// doesn't have a tiles row).
export const CHAIRMAN_HEADLINE = HEADLINE;

// Re-export rollupAll so the docx builder can pull live category YTDs
// without the page having to thread the data down.
export { rollupAll };

// ---------------------------------------------------------------------------
// Print CSS — A4 portrait, dense.
// ---------------------------------------------------------------------------

const printCss = `
  .report-toolbar {
    display: flex; justify-content: space-between; align-items: center;
    padding: 10px 14px; margin-bottom: 18px;
    background: var(--panel-2); border: 1px solid var(--edge);
    border-radius: 4px; color: var(--ink-dim); font-size: 12px;
  }
  .report-toolbar-right { display: flex; align-items: center; gap: 8px; }
  .report-print {
    font-family: var(--font-mono); font-size: 10px; letter-spacing: 0.12em;
    text-transform: uppercase; font-weight: 700;
    padding: 7px 14px; border-radius: 3px;
    border: 1px solid var(--purple); background: var(--purple);
    color: white; cursor: pointer;
  }
  .report-btn {
    font-family: var(--font-mono); font-size: 10px; letter-spacing: 0.12em;
    text-transform: uppercase; font-weight: 700;
    padding: 7px 12px; border-radius: 3px;
    border: 1px solid var(--edge); background: transparent;
    color: var(--ink); cursor: pointer;
  }
  .rep-live-chip {
    display: inline-block; margin-left: 10px;
    font-family: var(--font-mono); font-size: 9px; letter-spacing: 0.14em;
    font-weight: 700; padding: 2px 7px; border-radius: 2px;
  }
  .dl-toast {
    font-family: var(--font-mono); font-size: 10px;
    letter-spacing: 0.06em; padding: 4px 8px; border-radius: 3px;
    margin-right: 4px;
  }
  .dl-toast-working { background: rgba(217,156,58,0.18); color: #d99c3a; }
  .dl-toast-ok      { background: rgba(45,138,79,0.18); color: #2d8a4f; }
  .dl-toast-err     { background: rgba(168,50,50,0.18); color: #c44a4a; }
  .rep-live-chip-on   { background: #2d8a4f; color: #fff; }
  .rep-live-chip-load { background: #d99c3a; color: #fff; }
  .rep-live-chip-off  { background: #b3b3b3; color: #fff; }

  .report-page {
    --ink: #1a1a1a; --ink-soft: #3a3a3a; --ink-faint: #6b6b6b;
    --line: #d9d9d4; --line-soft: #ebeae5; --paper: #ffffff;
    --supply: #5E4AA8;
    --demand: #D14B8F;
    --question-grey: #6B6F76;
    --track-shade: #F5F4F8;
    --status-done-bg: #5E4AA8;     --status-done-fg: #FFFFFF;
    --status-live-bg: #5E4AA8;     --status-live-fg: #FFFFFF;
    --status-wip-bg: #D14B8F;      --status-wip-fg: #FFFFFF;
    --status-testing-bg: #D14B8F;  --status-testing-fg: #FFFFFF;
    --status-pending-bg: #E8E6EF;  --status-pending-fg: #5B5B66;
    --status-not-started-bg: #EFEEF3; --status-not-started-fg: #9AA0A6;
    --status-blocked-bg: #2E2E33;  --status-blocked-fg: #FFFFFF;
    --blocker: #A83232;

    /* Note: the article stays content-box (browser default). Forcing
       border-box on descendants in v0.8.13 collapsed inner chart
       widths and html-to-image returned empty PNGs — breaking both
       downloads. The right-drift fix lives entirely in the capture
       code (chairman-pdf.ts) via an explicit width/height + a
       margin:0 style override during the clone, which is enough. */
    width: 210mm; min-height: 297mm; margin: 0 auto;
    padding: 11mm 13mm 8mm;
    background: var(--paper); color: var(--ink);
    font-family: -apple-system, "SF Pro Text", system-ui, sans-serif;
    line-height: 1.42;
    font-size: 9.5px;
  }

  .report-head {
    border-bottom: 1px solid var(--ink); padding-bottom: 6px;
    margin-bottom: 8px;
  }
  .report-eyebrow {
    font-family: "SF Mono", Menlo, monospace; font-size: 8.5px;
    letter-spacing: 0.18em; color: var(--ink-faint); font-weight: 700;
    text-transform: uppercase; margin-bottom: 3px;
  }
  .report-title {
    font-size: 18px; font-weight: 700; color: var(--ink);
    margin: 0 0 3px; letter-spacing: -0.005em;
  }
  .report-meta {
    display: flex; gap: 6px; font-size: 8.5px; color: var(--ink-faint);
    font-family: "SF Mono", Menlo, monospace; letter-spacing: 0.05em;
  }

  /* KPI block — flex two-column layout. LEFT = title band + question +
     chart + track owners. RIGHT = OWNERS / INSIGHT / BLOCKERS / DOING
     stacked together, sitting next to the track owners (per source).
     Renamed from .kpi to .cb-kpi to avoid clashing with suite.css's
     global .kpi card primitive (which has bg/border/radius). */
  .cb-kpi {
    margin-top: 10px;
    page-break-inside: avoid;
    background: transparent;
    border: none;
    padding: 0;
  }
  .cb-kpi:first-of-type { margin-top: 6px; }

  .kpi-row {
    display: flex;
    gap: 14px;
    align-items: stretch;
  }
  .kpi-col-left  { flex: 0 0 62%; min-width: 0; }
  .kpi-col-right {
    flex: 1; min-width: 0;
    display: flex; flex-direction: column; gap: 7px;
    padding-top: 4px;
  }

  .kpi-chart { padding: 0; }
  .kpi-tracks {
    display: flex; flex-direction: column;
    margin-top: 4px;
  }

  /* Side banner — gradient supply→demand colours, plain bar */
  .side-banner {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 10px;
    margin: 10px 0 6px;
    color: #fff;
  }
  .side-banner-supply {
    background: linear-gradient(90deg, var(--supply) 0%, var(--demand) 100%);
  }
  .side-banner-demand {
    background: linear-gradient(90deg, var(--demand) 0%, var(--supply) 100%);
  }
  .side-banner-chip {
    font-family: "SF Mono", Menlo, monospace;
    font-size: 9px; letter-spacing: 0.14em; font-weight: 700;
    background: rgba(255,255,255,0.18);
    padding: 1.5px 6px; border-radius: 2px;
  }
  .side-banner-title {
    font-size: 11px; font-weight: 700; letter-spacing: 0.04em;
  }
  .side-banner-sub {
    font-size: 9.5px; font-style: italic; opacity: 0.92;
    margin-left: auto;
  }

  /* Inter-KPI divider — thin gradient line */
  .kpi-divider {
    height: 1px;
    margin: 6px 0;
    background: linear-gradient(90deg, var(--supply), var(--demand));
    opacity: 0.55;
  }

  /* Title band — coloured strip on the title row only; question sits
     unshaded below. */
  .kpi-band {
    padding: 5px 10px;
    display: flex; align-items: baseline; gap: 8px;
    color: #fff;
  }
  .kpi-band-supply { background: var(--supply); }
  .kpi-band-demand { background: var(--demand); }
  .kpi-chip {
    color: #fff; font-weight: 700;
    font-size: 10px; letter-spacing: 0.06em;
  }
  .kpi-title { font-weight: 700; font-size: 12px; color: #fff; }
  .kpi-question {
    font-size: 9.5px; color: var(--question-grey); font-style: italic;
    padding: 4px 0 5px;
  }

  /* Track-owner rows — flat on paper, no grey shading. */
  .kpi-track-row {
    display: flex; align-items: baseline; gap: 6px;
    padding: 2px 0;
    font-size: 9px;
  }
  .kpi-track-name { font-weight: 700; color: var(--ink); }
  .kpi-track-sep { color: var(--ink-faint); }
  .kpi-track-scope { color: var(--ink-soft); }
  .kpi-track-status {
    margin-left: auto;
    font-family: "SF Mono", Menlo, monospace; font-size: 8px;
    letter-spacing: 0.12em; font-weight: 700;
    padding: 2px 7px; border-radius: 3px;
  }
  .kpi-track-status-done, .kpi-track-status-live {
    background: var(--status-done-bg); color: var(--status-done-fg);
  }
  .kpi-track-status-wip, .kpi-track-status-testing {
    background: var(--status-wip-bg); color: var(--status-wip-fg);
  }
  .kpi-track-status-pending {
    background: var(--status-pending-bg); color: var(--status-pending-fg);
  }
  .kpi-track-status-not-started {
    background: var(--status-not-started-bg); color: var(--status-not-started-fg);
  }
  .kpi-track-status-blocked {
    background: var(--status-blocked-bg); color: var(--status-blocked-fg);
  }

  /* Right-column meta blocks — OWNERS / INSIGHT / BLOCKERS / DOING */
  .kpi-meta { display: flex; flex-direction: column; gap: 2px; }
  .kpi-meta-label {
    font-family: "SF Mono", Menlo, monospace; font-size: 7.5px;
    letter-spacing: 0.10em; font-weight: 700; color: var(--ink-faint);
    text-transform: uppercase;
  }
  .kpi-meta-label-bad { color: var(--blocker); }
  .kpi-meta-val { color: var(--ink-soft); font-size: 9.5px; line-height: 1.42; }
  .kpi-meta-val-bad { color: var(--blocker); }
  .kpi-meta-val strong { color: var(--ink); font-weight: 700; }

  /* Additional commitments */
  .addl {
    border-top: 1px solid var(--ink); margin-top: 6px; padding-top: 5px;
    display: flex; flex-direction: column; gap: 3px;
  }
  .addl-head {
    font-family: "SF Mono", Menlo, monospace; font-size: 9.5px;
    letter-spacing: 0.16em; font-weight: 700; color: var(--ink);
    text-transform: uppercase; margin-bottom: 2px;
  }
  .addl-row {
    display: flex; flex-direction: column; gap: 1px;
    padding: 3px 0;
    border-top: 0.5px dashed var(--line);
  }
  .addl-row:first-of-type { border-top: none; padding-top: 2px; }
  .addl-row-head { display: flex; gap: 8px; align-items: baseline; }
  .addl-row-title { font-weight: 700; font-size: 10px; color: var(--ink); }
  .addl-status {
    font-family: "SF Mono", Menlo, monospace; font-size: 7.5px;
    font-weight: 700; letter-spacing: 0.12em;
    padding: 1px 4px; border-radius: 2px; color: white;
  }
  .addl-status-done { background: var(--status-done); }
  .addl-status-wip { background: var(--status-wip); }
  .addl-status-pending { background: var(--status-pending); }
  .addl-status-blocked { background: var(--status-blocked); }
  .addl-owner {
    font-size: 9px; color: var(--ink-faint); margin-left: auto;
    font-family: "SF Mono", Menlo, monospace; letter-spacing: 0.05em;
  }
  .addl-body { font-size: 9.5px; color: var(--ink-soft); line-height: 1.45; }

  /* Signoff */
  .report-signoff {
    margin-top: 6px; padding-top: 4px; border-top: 1px solid var(--line);
  }
  .signoff-row { display: grid; grid-template-columns: 1.2fr 1fr 1.6fr; gap: 14px; }
  .signoff-block { display: flex; flex-direction: column; gap: 2px; }
  .signoff-label {
    font-family: "SF Mono", Menlo, monospace; font-size: 8px;
    letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--ink-faint); font-weight: 700;
  }
  .signoff-line { border-bottom: 1px solid var(--ink); height: 12px; }
  .signoff-note-lines { margin-top: 2px; display: flex; flex-direction: column; gap: 6px; }
  .signoff-note-lines > div { height: 10px; border-bottom: 0.5px solid var(--line); }

  .report-foot {
    margin-top: 6px; padding-top: 4px; border-top: 0.5px solid var(--line);
    display: flex; justify-content: space-between;
    font-family: "SF Mono", Menlo, monospace; font-size: 8px;
    letter-spacing: 0.12em; text-transform: uppercase; color: var(--ink-faint);
  }

  @media print {
    .no-print { display: none !important; }
    html, body { background: white !important; }
    body > * { visibility: hidden; }
    .report-page, .report-page * { visibility: visible; }
    .report-page {
      width: auto; min-height: auto; margin: 0;
      padding: 10mm 11mm 7mm;
      box-shadow: none; border-radius: 0;
      position: absolute; left: 0; top: 0; right: 0;
    }
    @page { size: A4 portrait; margin: 0; }
  }
`;
